import { Box, Popover, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { numberToUSDCurrency } from '../helpers/numberToUSDCurrency';
import { SettlementDistribution } from '../types';

const useStyles = makeStyles()((theme: Theme) => ({
  popover: {
    pointerEvents: 'none',
  },
  paper: {
    pointerEvents: 'auto',
    padding: '16px',
    background: theme.palette.secondary.dark,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
    width: '280px',
  },
  header: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.common.white,
    marginBottom: '12px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  label: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 400,
    color: theme.palette.common.white,
  },
  value: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 400,
    color: theme.palette.common.white,
  },
  divider: {
    height: '1px',
    background: theme.palette.primary.main,
    margin: '8px 0',
  },
  footerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '4px',
  },
  footerLabel: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.common.white,
  },
  footerValue: {
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.common.white,
  },
}));

interface WaveSettledAmountHoverCardProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  distribution: SettlementDistribution | null;
  total?: number;
}

const WaveSettledAmountHoverCard = ({
  anchorEl,
  open,
  onClose,
  onMouseEnter,
  distribution,
  total,
}: WaveSettledAmountHoverCardProps) => {
  const styles = useStyles();

  // Extract data from distribution or use defaults
  const sequenceNum = distribution?.sequence ?? 1;
  const totalSettlement = total ?? 0;
  const leadCounselName = distribution?.attorneysFees?.leadFirm?.name
    ? `${distribution.attorneysFees.leadFirm.name} (Lead Counsel)`
    : 'Lead Counsel';
  const leadCounselAmount = distribution?.attorneysFees?.leadFirm?.totalPaid ?? 0;
  const coCounselAmount = distribution?.attorneysFees?.secondaryFirmsCoCounselTotalPaid ?? 0;
  const referringCounselAmount = distribution?.attorneysFees?.secondaryFirmsReferingCounselTotalPaid ?? 0;
  const liensPartial = distribution?.deductions?.liens ?? 0;
  const serviceProviderPartial = distribution?.deductions?.serviceProviders ?? 0;
  const expensesPartial = distribution?.deductions?.advances ?? 0;
  const clientReceived = distribution?.netSettlement?.amount ?? 0;
  const settledAmount = distribution?.currentDisbursement ?? 0;
  const balance = distribution?.balance ?? 0;
  const previousDisbursement = distribution?.previousDisbursement ?? 0;

  return (
    <Popover
      className={styles.classes.popover}
      classes={{
        paper: styles.classes.paper,
      }}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={onClose}
      disableRestoreFocus
      slotProps={{
        paper: {
          onMouseEnter: onMouseEnter,
          onMouseLeave: onClose,
        },
      }}
    >
      <Box>
        <Typography className={styles.classes.header}>Wave {sequenceNum} - Settled Amount</Typography>

        {/* First Row with divider below */}
        <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>Total Settlement</Typography>
          <Typography className={styles.classes.value}>
            {numberToUSDCurrency(totalSettlement)}
          </Typography>
        </Box>
        {sequenceNum > 1 && (
          <Box className={styles.classes.row}>
            <Typography className={styles.classes.label}>
              {`Paid Out${sequenceNum > 2 ?
                ` (W${Array.from({ length: sequenceNum - 1 }, (_, i) => i + 1).join(' + W')})` :
                ' (W1)'}`}
            </Typography>
            <Typography className={styles.classes.value}>
              -{numberToUSDCurrency(previousDisbursement)}
            </Typography>
          </Box>
        )}
        <Box className={styles.classes.divider} />

        {/* Breakdown Rows */}
        {leadCounselAmount > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>{leadCounselName}</Typography>
          <Typography className={styles.classes.value}>
            -{numberToUSDCurrency(leadCounselAmount)}
          </Typography>
        </Box>}

        {coCounselAmount > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>Co-Counsel</Typography>
          <Typography className={styles.classes.value}>
            -{numberToUSDCurrency(coCounselAmount)}
          </Typography>
        </Box>}

        {referringCounselAmount > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>Referring Counsel</Typography>
          <Typography className={styles.classes.value}>
            -{numberToUSDCurrency(referringCounselAmount)}
          </Typography>
        </Box>}

        {liensPartial > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>Liens </Typography>
          <Typography className={styles.classes.value}>
            -{numberToUSDCurrency(liensPartial)}
          </Typography>
        </Box>}

        {serviceProviderPartial > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>Services Provider </Typography>
          <Typography className={styles.classes.value}>
            -{numberToUSDCurrency(serviceProviderPartial)}
          </Typography>
        </Box>
        }

        {expensesPartial > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>Expenses </Typography>
          <Typography className={styles.classes.value}>
            -{numberToUSDCurrency(expensesPartial)}
          </Typography>
        </Box>}

        {clientReceived > 0 && <Box className={styles.classes.row}>
          <Typography className={styles.classes.label}>Client Received  {`(W${sequenceNum})`}</Typography>
          <Typography className={styles.classes.value}>
            -{numberToUSDCurrency(clientReceived)}
          </Typography>
        </Box>}

        {/* Divider before footer summary */}
        <Box className={styles.classes.divider} />

        {/* Footer Summary */}
        <Box className={styles.classes.footerRow}>
          <Typography className={styles.classes.footerLabel}>Payout {`(W${sequenceNum})`}</Typography>
          <Typography className={styles.classes.footerValue}>
            -{numberToUSDCurrency(settledAmount)}
          </Typography>
        </Box>

        <Box className={styles.classes.footerRow}>
          <Typography className={styles.classes.footerLabel}>Balance</Typography>
          <Typography className={styles.classes.footerValue}>
            {numberToUSDCurrency(balance)}
          </Typography>
        </Box>
      </Box>
    </Popover>
  );
};

export default WaveSettledAmountHoverCard;
